import React, { useState } from 'react';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { useMyContext } from '../../../context/MyContext';
import * as Papa from 'papaparse';
import { Base_Url, uploadPincode } from '../../../config/config';
import toast from 'react-hot-toast';
import { FiEye } from 'react-icons/fi';
import { FiDownload } from 'react-icons/fi';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import FullPageLoader from '../../../component/fullPageLoader/fullPageLoader';

export default function UploadPincodeComponent() {
  const [file, setFile] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('bluedart');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const partners = [
    { value: 'bluedart', label: 'BlueDart', icon: '🚚' },
    { value: 'delhivery', label: 'Delhivery', icon: '🚚' },
    { value: 'ecom', label: 'Ecom Express', icon: '🚚' },
    { value: 'ekart', label: 'Ekart', icon: '🚚' },
    { value: 'xpressbees', label: 'XpressBees', icon: '🚚' }
  ];

  const API_CONFIG = {
    endpoint: Base_Url + uploadPincode,
    cus_id: cus_id,
    token: token
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setSuccess('');

    if (selectedFile && selectedFile.type === 'text/csv') {
      Papa.parse(selectedFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            const headers = results.data[0];
            const dataRows = results.data.slice(1).filter(row => row.some(cell => cell.trim() !== ''));

            const parsedData = dataRows.map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] || '';
              });
              return obj;
            });

            setPreviewData(parsedData);
          }
        },
        header: false,
        skipEmptyLines: true,
        error: (error) => {
          setError('Error parsing CSV file: ' + error.message);
        }
      });
    }
  };

  const openPreviewModal = () => {
    if (previewData && previewData.length > 0) {
      setShowPreview(true);
    } else {
      setError('No valid data to preview');
    }
  };

  const closePreviewModal = () => {
    setShowPreview(false);
  };

  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner.value);
    setIsDropdownOpen(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      toast.error('Please select a file first');
      return;
    }
    if (!previewData || previewData.length === 0) {
      setError('No valid data to upload');
      toast.error('No valid data to upload');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccess('');
    setIsPageLoading(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('partner', selectedPartner);

      const response = await fetch(API_CONFIG.endpoint, {
        method: 'POST',
        headers: {
          'cus_id': API_CONFIG.cus_id,
          'token': API_CONFIG.token,
        },
        body: formData,
      });
      const result = await response.json();

      if (response.ok) {
        setSuccess('File uploaded successfully!');
        toast.success('File uploaded successfully!');
        setFile(null);
        setPreviewData(null);
        setShowPreview(false);
        document.querySelector('input[type="file"]').value = '';
        setIsPageLoading(false);
      } else {
        const message = result?.messages?.csv_file || result?.message || 'Upload failed';
        setError(message);
        toast.error(message);
        setIsPageLoading(false);
      }
    } catch (error) {
      const message = error?.message || 'Upload failed';
      setError('Upload failed: ' + message);
      toast.error('Upload failed: ' + message);
      setIsPageLoading(false);
    } finally {
      setIsUploading(false);
      setIsPageLoading(false);
    }
  };

  // Custom Dropdown Component
  const PartnerDropdown = () => {
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="relative">
        <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Select Delivery Partner
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`relative w-full border rounded-xl pl-3 pr-9 py-2.5 text-left cursor-pointer transition-all duration-300 focus:outline-none ${
              isDarkMode
                ? 'bg-white/[0.03] border-white/10 text-white focus:border-indigo-500/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20'
                : 'bg-white border-gray-300 text-black focus:ring-1 focus:ring-black'
            }`}
          >
            <span className="flex items-center">
              <span className="text-sm mr-2">{selectedPartnerObj?.icon}</span>
              <span className={`block truncate font-semibold text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>
                {selectedPartnerObj?.label}
              </span>
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className={`h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div className={`absolute z-50 mt-1 w-full backdrop-blur-xl shadow-2xl rounded-xl border overflow-hidden ${
              isDarkMode
                ? 'bg-[#111827]/95 border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.5)]'
                : 'bg-white border-gray-200'
            }`}>
              <div className="py-1">
                {partners.map((partner) => (
                  <button
                    key={partner.value}
                    type="button"
                    onClick={() => handlePartnerSelect(partner)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center ${
                      selectedPartner === partner.value
                        ? (isDarkMode ? 'bg-indigo-500/15 text-indigo-300' : 'bg-gray-100 text-black')
                        : (isDarkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50')
                    }`}
                  >
                    <span className="text-sm mr-2">{partner.icon}</span>
                    <span className="font-semibold">{partner.label}</span>
                    {selectedPartner === partner.value && (
                      <span className="ml-auto">
                        <CheckCircle className={`h-3.5 w-3.5 ${isDarkMode ? 'text-indigo-400' : 'text-black'}`} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Preview Modal Component
  const PreviewModal = () => {
    if (!previewData || previewData.length === 0) return null;

    const headers = Object.keys(previewData[0]);
    const selectedPartnerObj = partners.find(p => p.value === selectedPartner);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className={`rounded-2xl shadow-2xl w-11/12 max-w-5xl max-h-[85vh] overflow-hidden border flex flex-col ${
          isDarkMode
            ? 'bg-gradient-to-br from-[#0B1120] to-[#111827] border-white/10 shadow-[0_0_60px_rgba(99,102,241,0.2)]'
            : 'bg-white border-gray-200'
        }`}>

          {/* Header */}
          <div className={`flex justify-between items-center px-5 py-3.5 border-b shrink-0 ${
            isDarkMode
              ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 border-white/10'
              : 'bg-slate-800 border-gray-200'
          }`}>
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">
                  Pincode Data Preview
                </h2>
                <p className="text-gray-200 text-[11px] flex items-center gap-1">
                  {selectedPartnerObj?.icon} {selectedPartnerObj?.label}
                </p>
              </div>
            </div>

            <button
              onClick={closePreviewModal}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content Area */}
          <div className="overflow-auto flex-1 p-4">

            {/* Data Stats */}
            <div className={`mb-3 rounded-xl border p-3 ${
              isDarkMode
                ? 'bg-white/[0.03] border-white/10'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Records: <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : 'text-black'}`}>{previewData.length}</span> ·
                  Columns: <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : 'text-black'}`}>{headers.length}</span> ·
                  Partner: <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : 'text-black'}`}>{selectedPartnerObj?.label}</span>
                </p>
                <p className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Showing first 50 rows</p>
              </div>
            </div>

            {/* Table */}
            <div className={`rounded-xl border overflow-hidden ${
              isDarkMode ? 'border-white/10' : 'border-gray-200'
            }`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`sticky top-0 ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-[#1F2937] via-[#1a2332] to-[#1F2937]'
                      : 'bg-slate-800'
                  }`}>
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          key={index}
                          className="p-2 text-left font-semibold text-white text-[10px] uppercase tracking-wide border-r border-white/10 last:border-r-0"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-gray-100'}`}>
                    {previewData.slice(0, 50).map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`transition-colors ${
                          isDarkMode
                            ? rowIndex % 2 === 0 ? 'bg-[#111827] hover:bg-[#1F2937]' : 'bg-[#0F172A] hover:bg-[#1F2937]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {headers.map((header, colIndex) => (
                          <td
                            key={colIndex}
                            className={`p-2 text-xs border-r last:border-r-0 ${
                              isDarkMode
                                ? 'text-gray-300 border-white/5'
                                : 'text-gray-700 border-gray-100'
                            }`}
                          >
                            <div className="truncate max-w-xs" title={row[header]}>
                              {row[header]}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer Info */}
            {previewData.length > 50 && (
              <div className={`text-center mt-3 p-2 rounded-xl border ${
                isDarkMode
                  ? 'bg-indigo-500/5 border-indigo-500/20'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <span className={`text-xs font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-gray-600'}`}>
                  Showing first 50 rows of {previewData.length} total rows
                </span>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className={`p-3.5 border-t shrink-0 ${
            isDarkMode
              ? 'bg-[#0F172A] border-white/10'
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center">
              <div className={`flex items-center space-x-1.5 text-xs ${
                isDarkMode ? 'text-emerald-300' : 'text-gray-600'
              }`}>
                <CheckCircle className={`w-3.5 h-3.5 ${isDarkMode ? 'text-emerald-400' : 'text-black'}`} />
                <span className="font-medium">Data loaded for {selectedPartnerObj?.label}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={closePreviewModal}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    isDarkMode
                      ? 'border-white/10 text-gray-300 hover:bg-white/5'
                      : 'border-gray-300 text-black hover:bg-gray-50'
                  }`}
                >
                  Close Preview
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-[0_0_20px_rgba(99,102,241,0.5)]'
                      : 'bg-slate-800 text-white hover:opacity-80'
                  }`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>{isUploading ? 'Uploading...' : 'Proceed with Upload'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
      isDarkMode ? 'dark bg-[#0B1120]' : 'bg-white'
    }`}>
      {showPreview && <PreviewModal />}

      <FullPageLoader
        isLoading={isPageLoading}
        message="Processing your request..."
      />

      {/* Premium ambient glows */}
      {isDarkMode ? (
        <>
          <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
          <div className="pointer-events-none absolute bottom-0 -right-32 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
        </>
      ) : (
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 blur-3xl" />
      )}

      <NavBar />
      <div className="flex flex-1 overflow-hidden relative z-10">
        {isToggleSidebar && (
          <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
            <Sidebar />
          </div>
        )}
        <div className={`flex-1 overflow-hidden flex flex-col transition-colors duration-300 ${
          isDarkMode ? 'bg-[#0B1120]' : 'bg-gray-100'
        }`}>
          <div className="flex-1 flex items-start justify-center p-4 overflow-hidden">
            <div className="w-full">
              <div className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                isDarkMode
                  ? 'bg-gradient-to-br from-[#111827] to-[#0F172A] border-gray-800 shadow-[0_0_40px_rgba(0,0,0,0.5)]'
                  : 'bg-white border-gray-200'
              }`}>

                {/* Premium Header */}
                <div className={`px-4 py-3.5 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600'
                    : 'bg-slate-800'
                }`}>
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
                      <Upload className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-white">
                      Upload Pincode List
                    </h2>
                  </div>
                  <p className="text-gray-200 text-[11px] ml-8 mt-0.5">Upload and process your pincode CSV file</p>
                </div>

                {/* Success/Error Messages */}
                {error && (
                  <div className={`mx-4 mt-3 p-2.5 rounded-xl border flex items-center ${
                    isDarkMode
                      ? 'bg-rose-500/10 border-rose-500/40'
                      : 'bg-white border-black'
                  }`}>
                    <X className={`w-3.5 h-3.5 mr-2 shrink-0 ${isDarkMode ? 'text-rose-400' : 'text-black'}`} />
                    <p className={`text-xs font-semibold ${isDarkMode ? 'text-rose-300' : 'text-black'}`}>{error}</p>
                  </div>
                )}

                {success && (
                  <div className={`mx-4 mt-3 p-2.5 rounded-xl border flex items-center ${
                    isDarkMode
                      ? 'bg-emerald-500/10 border-emerald-500/40'
                      : 'bg-black border-black'
                  }`}>
                    <CheckCircle className={`w-3.5 h-3.5 mr-2 shrink-0 ${isDarkMode ? 'text-emerald-400' : 'text-white'}`} />
                    <p className={`text-xs font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-white'}`}>{success}</p>
                  </div>
                )}

                {/* Partner Selection */}
                <div className='mt-4 mx-4'>
                  <PartnerDropdown />
                </div>

                {/* File Upload Section */}
                <div className="p-4">
                  <div className="mb-3">
                    <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Upload Your CSV File
                    </label>

                    {/* Drop Zone */}
                    <div className={`relative border border-dashed rounded-2xl p-5 transition-all duration-300 cursor-pointer ${
                      isDarkMode
                        ? 'bg-white/[0.02] border-white/15 hover:bg-white/[0.04] hover:border-indigo-500/60 hover:shadow-[0_0_25px_rgba(99,102,241,0.15)]'
                        : 'bg-white border-gray-300 hover:border-black hover:bg-gray-50'
                    }`}>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />

                      <div className="text-center">
                        <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2.5 ${
                          isDarkMode
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(99,102,241,0.4)]'
                            : 'bg-slate-800'
                        }`}>
                          <Upload className="w-4 h-4 text-white" />
                        </div>
                        <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                          Drop your CSV file here or click to browse
                        </p>
                        <p className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Supports CSV files up to 10MB
                        </p>

                        {file && (
                          <div className={`mt-3 p-2 rounded-xl border inline-flex items-center gap-1.5 ${
                            isDarkMode
                              ? 'bg-emerald-500/10 border-emerald-500/40'
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <CheckCircle className={`w-3.5 h-3.5 ${isDarkMode ? 'text-emerald-400' : 'text-black'}`} />
                            <span className={`font-semibold text-xs ${isDarkMode ? 'text-emerald-300' : 'text-black'}`}>{file.name}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row gap-2 mt-3">
                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={!file || isUploading}
                        className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition-all duration-300 ${
                          !file || isUploading
                            ? (isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-500' : 'bg-gray-200 cursor-not-allowed text-gray-400')
                            : (isDarkMode
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-[0_0_25px_rgba(99,102,241,0.5)] hover:scale-[1.02]'
                                : 'bg-slate-800 text-white hover:opacity-80')
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-1.5">
                          {isUploading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <Upload className="w-3.5 h-3.5" />
                          )}
                          <span>{isUploading ? 'Processing...' : 'Upload & Process'}</span>
                        </div>
                      </button>

                      {file && previewData && (
                        <button
                          type="button"
                          onClick={openPreviewModal}
                          className={`flex-1 md:flex-none md:px-4 py-2.5 rounded-xl font-bold text-xs border transition-all duration-300 ${
                            isDarkMode
                              ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 hover:shadow-[0_0_15px_rgba(99,102,241,0.4)]'
                              : 'border-black text-black hover:bg-black hover:text-white'
                          }`}
                        >
                          <div className="flex items-center justify-center space-x-1.5">
                            <FiEye className="w-3.5 h-3.5" />
                            <span>Preview Data</span>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Template Download */}
                  <div className="mt-3">
                    <div className={`rounded-2xl p-4 border transition-all duration-300 ${
                      isDarkMode
                        ? 'bg-white/[0.02] border-white/10'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center space-x-2.5">
                          <div className={`p-2 rounded-lg ${
                            isDarkMode
                              ? 'bg-sky-500/10 border border-sky-500/30'
                              : 'bg-white border border-gray-200'
                          }`}>
                            <FileText className={`w-3.5 h-3.5 ${isDarkMode ? 'text-sky-400' : 'text-black'}`} />
                          </div>
                          <div>
                            <h3 className={`font-bold text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>Need a template?</h3>
                            <p className={`text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Download our pre-formatted CSV template</p>
                          </div>
                        </div>
                        <a
                          href="/sampleFile/pincodeSample.csv"
                          download="pincodeSample.csv"
                          className={`px-3.5 py-2 rounded-xl font-bold text-xs transition-all duration-300 flex items-center space-x-1.5 ${
                            isDarkMode
                              ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:shadow-[0_0_20px_rgba(16,185,129,0.5)]'
                              : 'bg-slate-800 text-white hover:opacity-80'
                          }`}
                        >
                          <FiDownload className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}